// Regras puras das duas telas de WEB-20: o período na query string, o que conta como
// pendência da fila e o que é um alerta "ativo". Sem JSX aqui, é o que dá para testar
// sem montar árvore.

import type {
  AdminAlert,
  AdminOverview,
  AlertSeverity,
  OverviewPeriod,
} from '../../api/queries/admin-overview.ts';
import { OVERVIEW_PERIODS } from '../../api/queries/admin-overview.ts';
import type { PillTone } from '../../components/kit/pill.tsx';
import { formatShortDate, formatTime } from '../../lib/dates.ts';

export interface AdminOverviewSearch {
  period?: OverviewPeriod;
}

/**
 * Campo OPCIONAL de propósito (mesma razão de `features/overview/search.ts`): período
 * obrigatório em `validateSearch` tornaria `search` obrigatório em todo
 * `navigate({ to: '/admin' })` do painel, inclusive nos redirects de gate de WEB-01.
 */
export function validateAdminOverviewSearch(search: Record<string, unknown>): AdminOverviewSearch {
  const value = search.period;
  return OVERVIEW_PERIODS.some((period) => period === value)
    ? { period: value as OverviewPeriod }
    : {};
}

/** Default `7d`: "hoje" esconde o fim de semana e 30 dias dilui o que mudou. */
export const DEFAULT_PERIOD: OverviewPeriod = '7d';

export function resolvePeriod(search: AdminOverviewSearch): OverviewPeriod {
  return search.period ?? DEFAULT_PERIOD;
}

export const PERIOD_LABELS: Record<OverviewPeriod, string> = {
  today: 'Hoje',
  '7d': '7 dias',
  '30d': '30 dias',
};

// ---------------------------------------------------------------------------
// Alertas
// ---------------------------------------------------------------------------

const SEVERITY_TONE: Record<AlertSeverity, PillTone> = {
  info: 'info',
  warning: 'wait',
  critical: 'bad',
};

export const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  info: 'Informativo',
  warning: 'Atenção',
  critical: 'Crítico',
};

export function severityTone(severity: AlertSeverity): PillTone {
  return SEVERITY_TONE[severity];
}

/** `acked_until` no futuro = silenciado; no passado = a janela de 24 h já venceu. */
export function isSilenced(alert: AdminAlert, now: Date = new Date()): boolean {
  return alert.acked_until !== null && new Date(alert.acked_until).getTime() > now.getTime();
}

/** Ativo de verdade: disparado E não silenciado. É o que a tela põe no topo. */
export function isFiring(alert: AdminAlert, now: Date = new Date()): boolean {
  return alert.active && !isSilenced(alert, now);
}

export function countFiring(
  alerts: AdminAlert[],
  severity: AlertSeverity,
  now: Date = new Date(),
): number {
  return alerts.filter((alert) => isFiring(alert, now) && alert.severity === severity).length;
}

export type AlertState = 'firing' | 'silenced' | 'resolved';

export function alertState(alert: AdminAlert, now: Date = new Date()): AlertState {
  if (isSilenced(alert, now)) {
    return 'silenced';
  }
  return alert.active ? 'firing' : 'resolved';
}

/**
 * Os três grupos que o croqui desenha como três painéis (`screens/admin/alertas.tsx`):
 * "Ativos", "Silenciados" e, bloco que o croqui conta na pill mas não lista, os
 * resolvidos. Cada grupo já sai ordenado por severidade e sinal mais recente.
 */
export interface AlertGroups {
  firing: AdminAlert[];
  silenced: AdminAlert[];
  resolved: AdminAlert[];
}

export function groupAlerts(alerts: AdminAlert[], now: Date = new Date()): AlertGroups {
  const sorted = sortAlerts(alerts, now);
  return {
    firing: sorted.filter((alert) => alertState(alert, now) === 'firing'),
    silenced: sorted.filter((alert) => alertState(alert, now) === 'silenced'),
    resolved: sorted.filter((alert) => alertState(alert, now) === 'resolved'),
  };
}

const STATE_RANK: Record<AlertState, number> = { firing: 0, silenced: 1, resolved: 2 };
const SEVERITY_RANK: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };

/**
 * A rota já devolve "ativos primeiro", mas `active` não desconta o silenciamento: um
 * alerta silenciado por 24 h continua `active` e subiria ao topo da lista que existe
 * justamente para mostrar **o que ainda espera decisão**. Ordem final: disparado →
 * silenciado → resolvido; dentro de cada grupo, severidade e sinal mais recente.
 */
export function sortAlerts(alerts: AdminAlert[], now: Date = new Date()): AdminAlert[] {
  return [...alerts].sort((a, b) => {
    const state = STATE_RANK[alertState(a, now)] - STATE_RANK[alertState(b, now)];
    if (state !== 0) {
      return state;
    }
    const severity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (severity !== 0) {
      return severity;
    }
    return new Date(b.last_seen).getTime() - new Date(a.last_seen).getTime();
  });
}

// ---------------------------------------------------------------------------
// A fila
// ---------------------------------------------------------------------------

/**
 * As quatro pendências do item 1 da task. Somadas, são o **foco** de `/admin`
 * (`spec/17` §8.2: "o número de pendências da fila"). `criticalAlerts` vem da outra
 * query e chega como `undefined` enquanto ela carrega ou falha, nesse caso a soma sai
 * sem ele em vez de inventar zero.
 */
export interface QueueCounts {
  kyc: number;
  withdrawals: number;
  divergences: number;
  criticalAlerts: number | undefined;
}

export function queueCounts(
  overview: AdminOverview,
  criticalAlerts: number | undefined,
): QueueCounts {
  return {
    kyc: overview.kyc.pending_count,
    withdrawals: overview.withdrawals.awaiting_count,
    divergences: overview.reconciliation.open_divergences,
    criticalAlerts,
  };
}

export function totalPending(counts: QueueCounts): number {
  return counts.kyc + counts.withdrawals + counts.divergences + (counts.criticalAlerts ?? 0);
}

/** `1234` → `20 min`, `93 600` → `26 h`. Idade da cabeça do outbox, em texto curto. */
export function formatAgeSeconds(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  if (safe < 60) {
    return `${safe} s`;
  }
  if (safe < 3600) {
    return `${Math.floor(safe / 60)} min`;
  }
  if (safe < 86_400) {
    return `${Math.floor(safe / 3600)} h`;
  }
  const days = Math.floor(safe / 86_400);
  return days === 1 ? '1 dia' : `${days} dias`;
}

/**
 * `12/09 15:00`, a marca de tempo curta que o croqui usa nas tabelas e nos KV do admin
 * (`screens/admin/alertas.tsx`). `formatDateTime` traz o ano e faz a coluna "Desde"
 * quebrar em duas linhas.
 */
export function shortStamp(value: string): string {
  return `${formatShortDate(value)} ${formatTime(value)}`;
}

/**
 * "há 2 dias", "há 21 min", a forma que o croqui usa nos hints da fila e no
 * "atualizado" do painel de período. `lib/dates.ts` só tem data absoluta e está fora de
 * `files_allowed`; se outra tela do admin precisar, sobe para lá (mesmo caminho da
 * WEB-51, item 12 das "Questões abertas" de lá).
 */
export function formatSince(value: string, now: Date = new Date()): string {
  const seconds = Math.round((now.getTime() - new Date(value).getTime()) / 1000);
  return seconds < 60 ? 'agora' : `há ${formatAgeSeconds(seconds)}`;
}

const PCT = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** `conversion_pct` chega em 0 a 100 com uma casa (`pct1` de `spec/13`). */
export function formatPct(value: number): string {
  return `${PCT.format(value)} %`;
}
